import { EFilter, ESearchPageType, EProjectType, EProjectStatus } from '@/helpers/enums'

export const defaultHoursRange = [1, 40]
export const defaultDurationRange = [1, 12]
export const defaultPageSize = 6

const orJoiner = (result) => {
  return result.length > 1 ? `( ${result.join(' OR ')} )` : result.join(' OR ')
}

const buildNumberQuery = (key, value) => {
  const result = value.split(',')
  return result.map((i) => `${key} = ${i}`)
}

const buildStringQuery = (key, value) => {
  const result = value.split(',')
  return result.map((i) => `${key} = "${decodeURIComponent(i)}"`)
}

const buildLiteralQuery = (key, value) => {
  const result = value.split(',')
  return result.map((i) => `${key} = ${i}`)
}

const buildLocationQuery = (key, value) => {
  const result = []
  const getUri = extractLocation(value)

  const locationQuery = getUri.resultLocation.map((i) => {
    const item = i.split(':')
    // const [countryCode, locality, distance = 0] = item

    let query = `country_code:${item[0]}`

    if (item.length === 3) {
      // query for city
      const locality = item[1]
      const distance = item[2] || 0
      query += `|locality:${locality}|distance:${distance}`
    } else if (item.length === 2) {
      // query for country
      const distance = item[1] || 0
      query += `|distance:${distance}`
    }

    return `(${EFilter.LOCATION} IN "${query}")`
  })

  result.push(...locationQuery)

  return result
}

const buildHoursQuery = (value) => {
  const result = value.split(',')

  if (result[0] > defaultHoursRange[0] && result[1] < defaultHoursRange[1]) {
    return `hours_per_week >= ${result[0]} AND hours_per_week <= ${result[1]}`
  } else if (result[0] > defaultHoursRange[0]) {
    return `hours_per_week >= ${result[0]}`
  } else if (result[1] < defaultHoursRange[1]) {
    return `hours_per_week <= ${result[1]}`
  }
}

const buildDurationQuery = (value) => {
  const MONTH_LENGTH = 30
  const result = value.split(',')
  const minLength = result[0] * MONTH_LENGTH
  const maxLength = result[1] * MONTH_LENGTH

  if (result[0] > defaultDurationRange[0] && result[1] < defaultDurationRange[1]) {
    return `duration >= ${minLength} AND duration <= ${maxLength}`
  } else if (result[0] > defaultDurationRange[0]) {
    return `duration >= ${minLength}`
  } else if (result[1] < defaultDurationRange[1]) {
    return `duration <= ${maxLength}`
  }
}

function buildWorkPrefsQuery(value) {
  const query = value.split(',').map((el) => `work_model_type = ${el}`)
  return `( ${query.join(' OR ')} )`
}

export async function searchProjects({
  context,
  type,
  pageSize,
  route,
  typedQuery,
  filters = [],
  query = {}
}) {
  const getQueries = { ...route.query, ...query }
  const applyPageSize = pageSize || defaultPageSize
  const runQuery = []

  const locationQueries = []

  if (type === EProjectType.PRIVATE && route.query?.[EFilter.OPEN_ONLY] !== 'false') {
    Object.assign(getQueries, { [EFilter.OPEN_ONLY]: 'true' })
  }

  for (const key in getQueries) {
    if (Object.hasOwnProperty.call(getQueries, key)) {
      const value = getQueries[key]

      if (value) {
        switch (key) {
          case EFilter.TEXT: {
            const fillQuery = buildStringQuery(key, value)
            runQuery.push(fillQuery)
            break
          }
          case EFilter.POOLS: {
            const fillQuery = buildNumberQuery(key, value)
            runQuery.push(orJoiner(fillQuery))
            break
          }
          case EFilter.LOCATION: {
            const fillQuery = buildLocationQuery(key, value)
            locationQueries.push(...fillQuery)
            break
          }
          case EFilter.USER_LOCATION: {
            const fillQuery = buildLocationQuery(key, value)
            locationQueries.push(...fillQuery)
            break
          }
          case EFilter.HOURS: {
            const fillQuery = buildHoursQuery(value)
            runQuery.push(fillQuery)
            break
          }
          case EFilter.DURATION: {
            const fillQuery = buildDurationQuery(value)
            runQuery.push(fillQuery)
            break
          }
          case EFilter.WORK_PREFS: {
            const fillQuery = buildWorkPrefsQuery(value)
            runQuery.push(fillQuery)
            break
          }
          case EFilter.OPEN_ONLY: {
            if (value === 'true') {
              const fillQuery = buildLiteralQuery(EFilter.STATUS, EProjectStatus.ACTIVE)
              runQuery.push(fillQuery)
            }
            break
          }
          case EFilter.VMS_ID: {
            const fillQuery = buildStringQuery(key, value)
            runQuery.push(fillQuery)
            break
          }
          default:
            break
        }
      }
    }
  }

  try {
    const searchTypeMethod = () => {
      switch (type) {
        case EProjectType.PUBLIC:
          return context.$api.elasticSearch.getPublicProjects
        case EProjectType.PRIVATE:
          return context.$api.elasticSearch.getProjects
      }
    }
    const postMethod = searchTypeMethod()
    if (locationQueries.length > 0) {
      const locationQueriesOr = orJoiner(locationQueries)
      runQuery.push(locationQueriesOr)
    }
    const searchQuery = runQuery.map((e) => (runQuery.length > 1 ? `${e}` : e)).join(' AND ')
    const generatedOffset = (getQueries.page - 1) * applyPageSize || 0

    const searchByPage = (type) => {
      switch (type) {
        case ESearchPageType.VMS_ID:
          return `( vms_id = "${decodeURIComponent(route.params.id)}" )`
        case ESearchPageType.CATEGORIES:
          return `( categories = ${Number(route.params.id)} )`
        case ESearchPageType.POOLS:
          return `( pools = ${Number(route.params.id)} )`
        case ESearchPageType.CITY:
          return `( city = "${decodeURIComponent(route.params.city.split('-').join(' '))}" )`
        case ESearchPageType.POOL_IN_CITY:
          // prettier-ignore
          return `( pools = ${Number(route.params.id)} ) AND ( city = "${decodeURIComponent(route.params.city.split('-').join(' '))}" )`
        default:
          return searchQuery
      }
    }

    const { data } = await postMethod({
      filters,
      query: searchByPage(typedQuery) || '',
      size: applyPageSize,
      offset: generatedOffset,
      sort: [
        { name: 'public_sort_order', dir: 'asc' },
        { name: 'score', dir: 'desc' },
        { name: 'created', dir: 'desc' }
      ]
    })

    return {
      currentOffset: generatedOffset,
      results: data.results,
      total: data.totalResults
    }
  } catch (err) {
    return Promise.reject(err)
  }
}

export const paginationLength = (total, size) => {
  const setSize = size || defaultPageSize
  const divide = total / setSize
  return Math.ceil(divide)
}

export const extractLocation = (value) => {
  const getUri = decodeURIComponent(value).split(',')

  return {
    resultLocation: getUri.filter((e) => e.split(':').length > 1)
  }
}
